import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';

/**
 * A number field that offers presets and still accepts anything typed.
 *
 * Same shape as `HttpMethodPicker`: the trigger IS the input, and the dropdown
 * fills it rather than replacing it. A plain `<select>` cannot express "one of
 * these, or a number you choose", and two controls side by side make the user
 * decide which one is authoritative before they can answer a simple question.
 *
 * Two behaviours copied deliberately from the method picker, because both are
 * the difference between a field that feels solid and one that does not:
 *
 *   * input is sanitised on EVERY keystroke, so it can never hold something
 *     invalid even mid-type;
 *   * blur always resolves to a valid value, so nothing downstream has to
 *     handle an empty or half-typed state.
 */
export default function EditableNumberPicker({
  value,
  onChange,
  presets = [],
  min = 1,
  max = Number.MAX_SAFE_INTEGER,
  suffix = '',
  ariaLabel,
  disabled = false,
  className = '',
  menuMinWidth = 130,
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(String(value ?? ''));
  const [pos, setPos] = useState({ top: 0, left: 0, width: menuMinWidth });
  const triggerRef = useRef(null);
  const inputRef = useRef(null);

  // Props win while the user is not editing. Without this a value clamped or
  // rejected upstream would leave the field showing what was typed rather than
  // what took effect — the field would be lying about the state.
  useEffect(() => {
    if (document.activeElement !== inputRef.current) setDraft(String(value ?? ''));
  }, [value]);

  const close = useCallback(() => setOpen(false), []);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    setPos({ top: r.bottom + 4, left: r.left, width: Math.max(menuMinWidth, r.width) });
  }, [open, menuMinWidth]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, close]);

  /** Digits only — there is no valid row count containing anything else. */
  const sanitize = (raw) => String(raw).replace(/[^0-9]/g, '');

  /**
   * Clamp rather than reject.
   *
   * Somebody typing 1000000 means "as many as possible", and there is an
   * obvious right answer to give them. Refusing the input and making them
   * guess the maximum would be worse than silently honouring the intent.
   */
  const commit = (raw) => {
    const digits = sanitize(raw);
    if (digits === '') {
      setDraft(String(value ?? min));
      return;
    }
    const n = Math.min(max, Math.max(min, Number(digits)));
    setDraft(String(n));
    if (n !== value) onChange(n);
  };

  const pick = (n) => {
    setDraft(String(n));
    close();
    if (n !== value) onChange(n);
  };

  const dropdown =
    open && typeof document !== 'undefined' && document.body
      ? createPortal(
          <>
            <div
              className="method-picker-backdrop"
              aria-hidden
              onMouseDown={(e) => {
                e.preventDefault();
                close();
              }}
            />
            <div
              className="dyn-var-suggestions"
              style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width, zIndex: 10000 }}
              role="listbox"
            >
              {presets.map((n) => (
                <div
                  key={n}
                  role="option"
                  aria-selected={n === value}
                  className={`dyn-var-suggestion-item${n === value ? ' dyn-var-suggestion-item--active' : ''}`}
                  style={{ fontSize: 12 }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    pick(n);
                  }}
                >
                  {n.toLocaleString()}
                  {suffix}
                </div>
              ))}
            </div>
          </>,
          document.body
        )
      : null;

  return (
    <span ref={triggerRef} className={`enp ${className}`}>
      <input
        ref={inputRef}
        className="enp-input"
        value={draft}
        disabled={disabled}
        inputMode="numeric"
        aria-label={ariaLabel}
        onChange={(e) => setDraft(sanitize(e.target.value))}
        onBlur={(e) => commit(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit(e.currentTarget.value);
            e.currentTarget.blur();
          }
        }}
      />
      {suffix && <span className="enp-suffix">{suffix}</span>}
      <button
        type="button"
        className="enp-caret"
        disabled={disabled}
        aria-label={`${ariaLabel} presets`}
        aria-expanded={open}
        onMouseDown={(e) => {
          e.preventDefault();
          if (!disabled) setOpen((o) => !o);
        }}
      >
        <ChevronDown size={12} />
      </button>
      {dropdown}
    </span>
  );
}
